import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import { ContextSession } from "../../services/ContextSession";
import logoInjupemp from "../../img/LogoInjupemp2026.png";
import {
  adjuntarDesignacionFirmada,
  apiRequest,
  buildApiUrl,
  confirmarDesignacionBeneficiarios,
  crearDesignacionEnProceso,
  getAccessToken,
  getDesignacionFirma,
  getDesignacionTokenStatus,
  guardarBeneficiariosDesignacion,
  mapDesignacionSearchToUi,
} from "./enrolamientoApi";
import {
  formatIdentificacionVisual,
  stripIdentificacionVisual,
} from "./identificacionUtils";

const UI_PRIMARY = "#19498c";
const STEPS = [
  "Verificacion",
  "Beneficiario",
  "Confirmacion",
  "Adjuntar",
  "Finalizado",
];
const OFICINA_NOMBRES = {
  1: "Tegucigalpa",
  2: "San Pedro Sula",
  3: "La Ceiba",
  4: "Choluteca",
  5: "Santa Rosa de Copán",
  6: "Santa Bárbara",
  7: "Juticalpa",
};
const emptyBen = {
  id: "",
  dni: "",
  n1: "",
  n2: "",
  a1: "",
  a2: "",
  parentesco: "",
  porcentaje: "",
};

const t = (v) => (v === undefined || v === null ? "" : String(v).trim());
const n = (v, f = 0) => (Number.isFinite(Number(v)) ? Number(v) : f);
const normalizeNombreInput = (v) => {
  const raw = v === undefined || v === null ? "" : String(v);
  const normalized = raw
    .replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, "")
    .toUpperCase();
  const collapsed = normalized.replace(/\s+/g, " ").replace(/^\s+/, "");
  if (!collapsed) return "";
  return /\s$/.test(normalized) ? `${collapsed.trimEnd()} ` : collapsed.trim();
};
const normalizeNombre = (v) => normalizeNombreInput(v).trim();
const sumPct = (rows = []) =>
  rows.reduce((acc, x) => acc + n(x.porcentaje, 0), 0);
const localId = () =>
  `b-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const buildUniqueUploadFile = (file) => {
  if (!(file instanceof File)) return file;
  const originalName = t(file?.name) || "adjunto.bin";
  const dot = originalName.lastIndexOf(".");
  const ext = dot > 0 ? originalName.slice(dot) : "";
  const base = dot > 0 ? originalName.slice(0, dot) : originalName;
  const cleanBase =
    (base || "adjunto")
      .replace(/[^\w\- ]+/g, "")
      .trim()
      .replace(/\s+/g, "_") || "adjunto";
  const stamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const rand = Math.random().toString(16).slice(2, 8);
  const suffix = `_${stamp}_${rand}`;
  const maxBaseLen = Math.max(1, 240 - ext.length - suffix.length);
  const uniqueName = `${cleanBase.slice(0, maxBaseLen)}${suffix}${ext}`;
  return new File([file], uniqueName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified || Date.now(),
  });
};
const formatFechaHistorico = (value) => {
  const raw = t(value);
  if (!raw || raw === "0000-00-00" || raw === "0000-00-00 00:00:00") return "";

  const dotNetDate = raw.match(/\/Date\((\d+)\)\//);
  if (dotNetDate) {
    const fromTicks = new Date(Number(dotNetDate[1]));
    return Number.isNaN(fromTicks.getTime())
      ? ""
      : fromTicks.toLocaleDateString("es-HN");
  }

  const ddmmyyyy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (ddmmyyyy) return `${ddmmyyyy[1]}/${ddmmyyyy[2]}/${ddmmyyyy[3]}`;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("es-HN");
};
const sanitizeAdjuntoPath = (path) => {
  const raw = t(path);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw.replace("/api/api/", "/api/");
  return raw.replace(/^\/api\/api\//i, "/api/");
};
const resolveAdjuntoUrl = (path) => {
  const sanitized = sanitizeAdjuntoPath(path);
  if (!sanitized) return "";
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const withoutApiPrefix = sanitized.startsWith("/api/")
    ? sanitized.slice(4)
    : sanitized;
  return buildApiUrl(withoutApiPrefix);
};
const mapAdjuntos = (rows = []) =>
  (rows || []).map((item, index) => {
    const nombre = item?.Nombre || item?.nombreArchivo || item?.nombre || "";
    const tipo = item?.Tipo || item?.tipo || "";
    const downloadPath =
      item?.download_url ||
      (item?.AdjuntoId
        ? `/Enrolamiento/adjuntos/${item.AdjuntoId}/descargar/`
        : item?.ruta || item?.Ruta || "");
    return {
      id: item?.AdjuntoId || `adj-${index}`,
      nombre,
      descripcion: item?.Descripcion || item?.descripcion || nombre,
      entidad: t(item?.Entidad || item?.entidad).toUpperCase(),
      registro: t(item?.Registro || item?.registro),
      tipo,
      fechaCrea: item?.FechaCrea || item?.fechaCrea || "",
      downloadUrl: resolveAdjuntoUrl(downloadPath),
    };
  });
const resolverNombreOficina = (oficinaSesion) => {
  const raw = String(oficinaSesion ?? "").trim();
  if (!raw) return OFICINA_NOMBRES[1];
  return OFICINA_NOMBRES[raw] || OFICINA_NOMBRES[Number(raw)] || raw;
};
const resolverLugarNacimientoReporte = (lugarNacimiento) => {
  return t(lugarNacimiento);
};
const stateColor = (s) =>
  s === "ACTIVO" ? "success" : s === "EN PROCESO" ? "warning" : "default";

const openPrintDialogDirect = (html) => {
  const reporteVentana = window.open("", "_blank", "width=960,height=700");
  if (!reporteVentana)
    throw new Error("No se pudo abrir la ventana de impresión.");
  reporteVentana.document.open();
  reporteVentana.document.write(html);
  reporteVentana.document.close();
};

const reportHtml = ({
  persona,
  des,
  firma,
  oficinaSesion,
  getParentescoLabel,
}) => {
  const fechaActual = new Date();
  const _MESES = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  const fechaLarga = `${fechaActual.getDate()} días de ${_MESES[fechaActual.getMonth()]} de ${fechaActual.getFullYear()}`;
  const horaBase = fechaActual.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const [horaSimple, meridiano] = horaBase.split(" ");
  const horaLarga = `${horaSimple} ${meridiano?.toLowerCase() === "am" ? "a.m." : "p.m."}`;
  const numeroActa = t(des?.numero ?? des?.Numero);
  const nombreAfiliado =
    [
      t(persona?.primerNombre),
      t(persona?.segundoNombre),
      t(persona?.primerApellido),
      t(persona?.segundoApellido),
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim() || t(persona?.nombreCompleto);
  const firmaDirectorImg = firma?.ImagenDataUrl || "";
  const nombreOficinaFirma = resolverNombreOficina(oficinaSesion);
  const lugarNacimientoReporte = resolverLugarNacimientoReporte(
    persona?.lugarNacimiento || persona?.lugarNacimientoReporte,
  );
  const fechaNacPartes = t(persona?.fechaNacimiento).split("-");
  const fechaNacimientoFormato =
    fechaNacPartes.length === 3
      ? `${fechaNacPartes[2]}-${fechaNacPartes[1]}-${fechaNacPartes[0]}`
      : t(persona?.fechaNacimiento);
  const filasBeneficiarios = (des?.beneficiarios || [])
    .map(
      (item) => `
          <tr>
            <td>${`${t(item.n1)} ${t(item.n2)} ${t(item.a1)} ${t(item.a2)}`.replace(/\s+/g, " ").trim()}</td>
            <td>${formatIdentificacionVisual(t(item.dni))}</td>
            <td>${t(getParentescoLabel(item.parentesco) || "BENEFICIARIO")}</td>
            <td style="text-align:right;">${n(item.porcentaje)}%</td>
          </tr>`,
    )
    .join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Constancia de Designacion de Beneficiarios Legales</title>
    <style>
      @page { size: legal portrait; margin: 14mm 14mm; }
      body { font-family: Arial, sans-serif; color: #111; font-size: 12px; margin: 0; background: #fff; }
      .toolbar { text-align: right; margin: 8px auto 10px; width: 7.35in; }
      .sheet { width: 7.35in; min-height: 12.9in; margin: 0 auto; display: flex; flex-direction: column; justify-content: space-between; }
      .main-block { padding-top: 0.08in; }
      .const-block { padding-bottom: 0.08in; }
      .header { display: flex; justify-content: center; align-items: center; margin-top: 0.12in; margin-bottom: 0.22in; }
      .logo { height: 68px; width: auto; object-fit: contain; }
      .title-row { display: flex; justify-content: space-between; align-items: baseline; margin: 0 0 0.16in; }
      .title-main { font-size: 13px; font-weight: 700; letter-spacing: .2px; }
      .title-num { font-size: 13px; font-weight: 700; }
      p { margin: 0 0 0.11in; line-height: 1.35; text-align: justify; }
      table { width: 100%; border-collapse: collapse; margin-top: 0.14in; margin-bottom: 0.2in; }
      th, td { border: 1px solid #777; padding: 4px 6px; font-size: 10.5px; }
      th { background: #AD8401; text-align: center; font-weight: 700; }
      .center { text-align: center; }
      .small-note { font-size: 9px; margin-top: 0.08in; }
      .firma-wrap { margin-top: 0.8in; text-align: center; }
      .firma-participante { margin-top: 0.1in; text-align: center; }
      .firma-line { border-top: 1px solid #000; width: 220px; margin: 0 auto 4px; }
      .firma-director-signature { min-height: 0.1in; margin: 0 auto 2px; display: flex; align-items: flex-end; justify-content: center; }
      .firma-director-signature img { width: 260px; max-width: 260px; max-height: 1.25in; object-fit: contain; }
      .firma-label { font-size: 9px; font-weight: 700; }
      .huella-space { position: relative; height: 1.05in; }
      .huella-mark { position: absolute; right: 1.45in; bottom: 0.03in; width: 0.52in; height: 0.74in; border: 1px dashed #8a8a8a; border-radius: 50% 50% 45% 45%; opacity: .45; }
      .divider { border-top: 1px dashed #666; margin: 0.16in 0 0.12in; }
      .const-title { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; margin-bottom: 0.1in; }
      .btn-print { background: #0c9a8e; color: #fff; border: 0; border-radius: 4px; padding: 7px 14px; font-size: 12px; cursor: pointer; }
      @media print { .toolbar { display: none; } body { margin: 0; } }
    </style>
  </head>
  <body>
    <div class="toolbar"><button class="btn-print" onclick="window.print()">Imprimir</button></div>
    <div class="sheet">
    <div class="main-block">
      <div class="header"><img class="logo" src="${logoInjupemp}" alt="INJUPEMP" /></div>
      <div class="title-row">
        <div class="title-main">SE DESIGNAN BENEFICIARIOS LEGALES</div>
        <div class="title-num">N° ${numeroActa}</div>
      </div>
      <p>Señor Director Especialista Presidente del Instituto Nacional de Jubilaciones y Pensiones de los Empleados y Funcionarios del Poder Ejecutivo (INJUPEMP):</p>
      <p>Yo <strong>${nombreAfiliado}</strong>, con tarjeta de identidad N° <strong>${formatIdentificacionVisual(t(persona?.dni))}</strong>, nacido en: <strong>${lugarNacimientoReporte || "-"}</strong>, el <strong>${fechaNacimientoFormato}</strong>. De Conformidad con los artículos 86, 87 y 89 de la Ley del Instituto Nacional de Jubilaciones y Pensiones de los Empleados y Funcionarios del poder Ejecutivo(INJUPEMP), y para los efectos Jurídicos Correspondientes Comparezco ante Usted a manifestar de forma libre y espontanea la designación de mis beneficiarios legales, los que acontinuación detallo:</p>
      <table>
        <thead>
          <tr>
            <th>Nombres y Apellidos</th>
            <th>No de identidad</th>
            <th>Parentesco / Otro</th>
            <th>Porcentaje</th>
          </tr>
        </thead>
        <tbody>
          ${filasBeneficiarios}
          <tr>
            <td colspan="3" style="text-align:right;font-weight:700;">TOTAL</td>
            <td style="text-align:right;font-weight:700;">${sumPct(des?.beneficiarios || [])}%</td>
          </tr>
        </tbody>
      </table>
      <p>La anterior designación la ratifico por ser mi expresa voluntad y así mismo dispongo que si alguno de los beneficiarios descritos falleciere, respecto al porcentaje a él asignado se esté a lo que dispone el artículo 89 de la ley de Instituto Nacional de Jubilaciones y Pensiones y funcionarios del Poder Ejecutivo (INJUPEMP) Me reservo el derecho de revocar o modificar la presente Designación de Beneficiarios Legales.</p>
      <p>Firmo en ${nombreOficinaFirma}, a los ${fechaLarga}, siendo las ${horaLarga}.</p>
      <div class="firma-participante">
        <div class="huella-space"><div class="huella-mark"></div></div>
        <div class="firma-line"></div>
        <div class="firma-label">FIRMA Y HUELLA DIGITAL</div>
      </div>
      <p class="small-note center">Nota: Asegúrese de escribir el nombre de los beneficiarios designados conforme aparece la certificación de acta de nacimiento o tarjeta de identidad respectiva y que los porcentajes distribuidos entre los beneficiarios designados, totalice el 100%.</p>
      <div class="firma-wrap">
        ${
          firmaDirectorImg
            ? `<div class="firma-director-signature"><img src="${firmaDirectorImg}" alt="Firma del Director Especialista Presidente" onerror="this.style.display='none';" /></div>`
            : `<div class="firma-director-signature"></div>`
        }
        <div class="firma-line"></div>
        <div class="firma-label">FIRMA Y SELLO DIRECTOR ESPECIALISTA PRESIDENTE</div>
      </div>
    </div>
    <div class="const-block">
      <div class="divider"></div>
      <div class="const-title">
        <span>CONSTANCIA DE DESIGNACIÓN BENEFICIARIOS LEGALES</span>
        <span>N° ${numeroActa}</span>
      </div>
      <p>El señor (a) <strong>${nombreAfiliado}</strong> ha presentado en esta fecha su designación de beneficiarios legales en el Instituto Nacional de Jubilaciones y Pensiones de los Empleados y Funcionarios del Poder Ejecutivo (INJUPEMP).</p>
      <p>Firmo en ${nombreOficinaFirma}, a los ${fechaLarga}, siendo las ${horaLarga}.</p>
      <div class="firma-wrap">
        <div class="firma-line"></div>
        <div class="firma-label">FIRMA Y SELLO ENCARGADO</div>
      </div>
    </div>
    </div>
  </body>
</html>`;
};

export default function ActualizarDesignacion({
  modoConsulta = false,
  titulo = "Actualizar Designacion de Beneficiarios Legales",
} = {}) {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";

  const [dni, setDni] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [newLoading, setNewLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const [result, setResult] = useState(null);
  const [designacionId, setDesignacionId] = useState(null);
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenSec, setTokenSec] = useState(0);
  const [autoRefreshToken, setAutoRefreshToken] = useState(false);
  const [firma, setFirma] = useState(null);
  const [adjunto, setAdjunto] = useState(null);
  const [adjuntos, setAdjuntos] = useState([]);
  const [tab, setTab] = useState(0);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [previewAdjuntoOpen, setPreviewAdjuntoOpen] = useState(false);
  const [previewAdjuntoUrl, setPreviewAdjuntoUrl] = useState("");
  const [previewAdjuntoMime, setPreviewAdjuntoMime] = useState("");
  const [previewAdjuntoError, setPreviewAdjuntoError] = useState("");
  const [previewAdjuntoLoading, setPreviewAdjuntoLoading] = useState(false);

  const [openBen, setOpenBen] = useState(false);
  const [benForm, setBenForm] = useState({ ...emptyBen });
  const [benErr, setBenErr] = useState({});
  const [catalogParentescos, setCatalogParentescos] = useState([]);
  const [loadingParentescos, setLoadingParentescos] = useState(false);
  const fileRef = useRef(null);

  const designaciones = result?.designaciones || [];
  const desActiva = designaciones.find((x) => x.estado === "ACTIVO") || null;
  const desProceso =
    designaciones.find((x) => x.estado === "EN PROCESO") || null;
  const nuevaDesignacionDeshabilitada = Boolean(desProceso) || newLoading || tokenLoading;
  const desSel =
    designaciones.find(
      (x) => String(x.idDesignacion) === String(designacionId),
    ) ||
    desProceso ||
    desActiva ||
    designaciones[0] ||
    null;
  const totalPct = sumPct(beneficiarios);
  const ocultarDesignacionPorConfidencialidad =
    !modoConsulta &&
    String(desSel?.estado || "").trim().toUpperCase() === "ACTIVO";
  const mostrarAccionesEdicion = !modoConsulta;
  const accionesFinalesHabilitadas =
    Boolean(beneficiarios.length) && totalPct === 100 && !dirty;
  const tokenVigente = Boolean(tokenInfo?.verificado && tokenSec > 0);
  const previewAdjuntoEsPdf =
    String(previewAdjuntoMime || "").toLowerCase().includes("pdf") ||
    /\.pdf$/i.test(t(successData?.adjunto?.nombre));
  const parentescoOptions = useMemo(() => {
    const mapped = (catalogParentescos || [])
      .map((item) => ({
        value: t(item?.ParentescoId ?? item?.parentescoId ?? item?.Descripcion),
        label: t(item?.Descripcion ?? item?.descripcion ?? item?.Nombre),
      }))
      .filter((item) => item.value && item.label);
    const unique = new Map();
    mapped.forEach((item) => {
      if (!unique.has(item.value)) unique.set(item.value, item);
    });
    return Array.from(unique.values());
  }, [catalogParentescos]);

  const getParentescoLabel = (value) => {
    const raw = t(value);
    if (!raw) return "";
    const found = parentescoOptions.find(
      (item) =>
        t(item.value) === raw ||
        t(item.label).toUpperCase() === raw.toUpperCase(),
    );
    return found?.label || raw;
  };

  const step = useMemo(() => {
    if (desActiva && !desProceso) return 4;
    if (totalPct === 100 && beneficiarios.length) return 3;
    if (beneficiarios.length && (desProceso || desActiva)) return 2;
    if (tokenVigente || desProceso || desActiva) return 1;
    return 0;
  }, [tokenVigente, desProceso, desActiva, totalPct, beneficiarios.length]);

  useEffect(
    () => setTokenSec(n(tokenInfo?.token?.segundosRestantes, 0)),
    [tokenInfo?.token?.tokenId, tokenInfo?.token?.segundosRestantes],
  );
  useEffect(() => {
    if (tokenSec <= 0) return undefined;
    const i = setInterval(() => setTokenSec((p) => (p > 0 ? p - 1 : 0)), 1000);
    return () => clearInterval(i);
  }, [tokenSec]);
  useEffect(() => {
    const rows = (desSel?.beneficiarios || []).map((b) => ({
      id: b.localId || localId(),
      dni: t(b.dni),
      n1: normalizeNombre(b.primerNombre),
      n2: normalizeNombre(b.segundoNombre),
      a1: normalizeNombre(b.primerApellido),
      a2: normalizeNombre(b.segundoApellido),
      parentesco: t(b.parentesco),
      porcentaje: n(b.porcentaje, 0),
    }));
    setBeneficiarios(rows);
    setDirty(false);
  }, [desSel?.idDesignacion, result]);

  useEffect(() => {
    let active = true;
    const loadParentescos = async () => {
      if (!authToken) return;
      try {
        setLoadingParentescos(true);
        const data = await apiRequest("/Enrolamiento/catalogos/", {
          authToken,
        });
        if (!active) return;
        const parentescos = data?.catalogos?.parentescos || [];
        setCatalogParentescos(Array.isArray(parentescos) ? parentescos : []);
      } catch (_err) {
        if (active) setCatalogParentescos([]);
      } finally {
        if (active) setLoadingParentescos(false);
      }
    };
    loadParentescos();
    return () => {
      active = false;
    };
  }, [authToken]);

  useEffect(() => {
    if (!autoRefreshToken || !result?.personaId || !authToken) return undefined;
    let active = true;
    const pollToken = async () => {
      try {
        const data = await getDesignacionTokenStatus(authToken, {
          personaId: result.personaId,
        });
        if (!active) return;
        setTokenInfo(data || null);
      } catch (_e) {
        // Silenciar error en polling automatico.
      }
    };
    pollToken();
    const timer = setInterval(pollToken, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [autoRefreshToken, authToken, result?.personaId]);
  useEffect(
    () => () => {
      if (previewAdjuntoUrl) URL.revokeObjectURL(previewAdjuntoUrl);
    },
    [previewAdjuntoUrl],
  );

  const refreshToken = async (personaId, documento) => {
    if (!personaId && !documento) return;
    setTokenLoading(true);
    try {
      const data = await getDesignacionTokenStatus(authToken, {
        personaId,
        dni: !personaId ? documento : undefined,
      });
      setTokenInfo(data || null);
      return data || null;
    } finally {
      setTokenLoading(false);
    }
  };

  const refreshAdjuntos = async (personaId) => {
    if (!personaId) return [];
    try {
      const data = await apiRequest(
        `/Enrolamiento/personas/${encodeURIComponent(personaId)}/adjuntos/?entidad=designacion`,
        { authToken },
      );
      const mapped = mapAdjuntos(data?.adjuntos || []);
      setAdjuntos(mapped);
      return mapped;
    } catch (_e) {
      setAdjuntos([]);
      return [];
    }
  };

  const loadByDni = async (dniValue, preferId = null, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await apiRequest(
        `/Enrolamiento/designaciones/buscar/?dni=${encodeURIComponent(dniValue)}&tipo=L`,
        { authToken },
      );
      const ui = mapDesignacionSearchToUi(data);
      setResult(ui);
      const sel =
        ui.designaciones.find(
          (x) => String(x.idDesignacion) === String(preferId),
        ) ||
        ui.designaciones.find((x) => x.estado === "EN PROCESO") ||
        ui.designaciones.find((x) => x.estado === "ACTIVO") ||
        ui.designaciones[0] ||
        null;
      setDesignacionId(sel?.idDesignacion ?? null);
      const [, adjuntosRefrescados] = await Promise.all([
        refreshToken(ui.personaId, ui.dni),
        refreshAdjuntos(ui.personaId),
      ]);
      if (!firma)
        setFirma(await getDesignacionFirma(authToken).catch(() => null));
      return {
        ui,
        selectedDesignacionId: sel?.idDesignacion ?? null,
        adjuntos: adjuntosRefrescados || [],
      };
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const buscar = async () => {
    if (!t(dni)) return setError("Ingrese el DNI.");
    try {
      setError("");
      setOk("");
      setAdjunto(null);
      await loadByDni(dni);
      setOk("Consulta realizada.");
    } catch (e) {
      setResult(null);
      setError(e?.message || "No se pudo consultar.");
      setOk("");
    }
  };

  const createProceso = async () => {
    if (!result?.personaId) return setError("Primero busque la persona.");
    if (newLoading) return;
    if (desProceso) {
      setError("Ya existe una designacion en EN PROCESO.");
      setOk("");
      return;
    }
    setNewLoading(true);
    try {
      let tokenActual = tokenInfo;
      if (!tokenVigente) {
        tokenActual = await refreshToken(result.personaId, result.dni || dni);
      }
      const tokenDisponible = Boolean(
        tokenActual?.verificado &&
        n(tokenActual?.token?.segundosRestantes, 0) > 0,
      );
      if (!tokenDisponible) {
        setError("No hay token vigente. Realice la verificacion de huella.");
        setOk("");
        return;
      }
      const r = await crearDesignacionEnProceso(authToken, result.personaId, {
        tipo: "L",
        token_id: tokenActual?.token?.tokenId,
      });
      const id = r?.designacion?.DesignacionId || r?.designacion?.idDesignacion;
      await loadByDni(result.dni || dni, id, true);
      setTab(0);
      setOk("Designacion creada en EN PROCESO.");
      setError("");
    } catch (e) {
      setError(e?.message || "No se pudo crear designacion.");
      setOk("");
    } finally {
      setNewLoading(false);
    }
  };

  const saveBeneficiariosApi = async (silent = false) => {
    if (!result?.personaId || !desSel?.idDesignacion)
      throw new Error("No hay designacion seleccionada.");
    if (desSel.estado !== "EN PROCESO")
      throw new Error("Solo puede editar designacion EN PROCESO.");
    const payload = beneficiarios.map((b) => ({
      dni: b.dni,
      primerNombre: b.n1,
      segundoNombre: b.n2,
      primerApellido: b.a1,
      segundoApellido: b.a2,
      parentesco: b.parentesco,
      tipoDocumento: "IDENTIDAD",
      porcentaje: n(b.porcentaje, 0),
    }));
    const resp = await guardarBeneficiariosDesignacion(
      authToken,
      result.personaId,
      desSel.idDesignacion,
      payload,
    );
    setDirty(false);
    await loadByDni(result.dni || dni, desSel.idDesignacion, true);
    if (!silent) {
      setOk(
        `Beneficiarios guardados. Total ${n(resp?.totalPorcentaje, totalPct)}%.`,
      );
      setError("");
    }
  };

  const saveBeneficiarios = async () => {
    try {
      setSaveLoading(true);
      await saveBeneficiariosApi(false);
    } catch (e) {
      setError(e?.message || "No se pudo guardar beneficiarios.");
      setOk("");
    } finally {
      setSaveLoading(false);
    }
  };

  const printNow = async () => {
    if (!desSel) return setError("No hay designacion seleccionada.");
    if (!beneficiarios.length || totalPct !== 100)
      return setError("Debe completar 100% para imprimir.");
    try {
      setPrintLoading(true);
      if (desSel.estado === "EN PROCESO" && dirty)
        await saveBeneficiariosApi(true);
      const f =
        firma || (await getDesignacionFirma(authToken).catch(() => null));
      if (!firma) setFirma(f);
      const desPrint = {
        ...desSel,
        beneficiarios: beneficiarios,
      };
      openPrintDialogDirect(
        reportHtml({
          persona: result,
          des: desPrint,
          firma: f,
          oficinaSesion: sessi?.sessionData?.oficina,
          getParentescoLabel,
        }),
      );
      setOk("Se abrio la impresion del navegador.");
      setError("");
    } catch (e) {
      setError(e?.message || "No se pudo imprimir.");
      setOk("");
    } finally {
      setPrintLoading(false);
    }
  };

  const abrirSelectorAdjunto = () => {
    fileRef.current?.click();
  };

  const adjuntarFinal = async () => {
    const archivoAdjunto = adjunto;
    if (!archivoAdjunto)
      return setError("Seleccione un archivo adjunto antes de finalizar.");
    if (!result?.personaId || !desSel?.idDesignacion)
      return setError("No hay designacion valida.");
    if (!beneficiarios.length || totalPct !== 100)
      return setError("El total debe ser 100% para activar.");
    const confirmar = window.confirm(
      "Se adjuntara el archivo y se finalizara la designacion. Desea continuar?",
    );
    if (!confirmar) return;
    const designacionIdActual = desSel.idDesignacion;
    const designacionNumeroActual = t(desSel?.numero ?? desSel?.Numero) || "-";
    const beneficiariosResumen = (beneficiarios || []).map((item) => ({
      ...item,
    }));
    const totalResumen = sumPct(beneficiariosResumen);
    const personaNombre =
      t(
        [
          result?.primerNombre,
          result?.segundoNombre,
          result?.primerApellido,
          result?.segundoApellido,
        ]
          .join(" ")
          .replace(/\s+/g, " "),
      ) || "la persona";
    try {
      setUploadLoading(true);
      if (desSel.estado === "EN PROCESO" && dirty)
        await saveBeneficiariosApi(true);
      const archivoAdjuntoUnico = buildUniqueUploadFile(archivoAdjunto);
      const uploadResp = await adjuntarDesignacionFirmada(
        authToken,
        result.personaId,
        archivoAdjuntoUnico,
        {
          designacionId: designacionIdActual,
          observaciones: "Archivo de designacion firmado",
        },
      );
      await confirmarDesignacionBeneficiarios(
        authToken,
        result.personaId,
        designacionIdActual,
      );
      const loadResp = await loadByDni(result.dni || dni, designacionIdActual, true);
      const adjuntosActualizados = loadResp?.adjuntos || [];
      setAdjunto(null);
      if (fileRef.current) fileRef.current.value = "";
      const adjuntoSubido = mapAdjuntos(
        uploadResp?.adjunto ? [uploadResp.adjunto] : [],
      )?.[0] || null;
      const adjuntoResumen =
        adjuntosActualizados.find(
          (item) =>
            String(item?.id || "") === String(adjuntoSubido?.id || "") ||
            t(item?.registro) === String(designacionIdActual),
        ) ||
        adjuntoSubido ||
        null;
      setSuccessData({
        personaName: personaNombre,
        designacionId: designacionIdActual,
        designacionNumero: designacionNumeroActual,
        beneficiarios: beneficiariosResumen,
        totalPorcentaje: totalResumen,
        adjunto: adjuntoResumen,
      });
      setSuccessOpen(true);
      setOk("Adjunto guardado y designacion finalizada.");
      setError("");
    } catch (e) {
      setError(e?.message || "No se pudo adjuntar/finalizar.");
      setOk("");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAdjuntoSeleccionado = (event) => {
    const archivo = event.target.files?.[0] || null;
    if (!archivo) return;
    setAdjunto(archivo);
    setOk(`Archivo seleccionado: ${archivo.name}`);
    setError("");
    event.target.value = "";
  };

  const openAdd = () => {
    if (desSel?.estado !== "EN PROCESO")
      return setError("Debe seleccionar una designacion EN PROCESO.");
    if (totalPct >= 100)
      return setError("El total de porcentaje legal ya llego a 100%.");
    setBenForm({ ...emptyBen });
    setBenErr({});
    setOpenBen(true);
  };

  const openEdit = (b) => {
    if (desSel?.estado !== "EN PROCESO") return;
    setBenForm({
      ...b,
      n1: normalizeNombre(b.n1),
      n2: normalizeNombre(b.n2),
      a1: normalizeNombre(b.a1),
      a2: normalizeNombre(b.a2),
      porcentaje: String(n(b.porcentaje, 0)),
    });
    setBenErr({});
    setOpenBen(true);
  };
  const handleBenNombreBlur = (field) => () => {
    setBenForm((prev) => {
      const normalizedValue = normalizeNombre(prev[field]);
      if (prev[field] === normalizedValue) return prev;
      return { ...prev, [field]: normalizedValue };
    });
  };

  const saveLocalBen = () => {
    const e = {};
    const pctRaw = t(benForm.porcentaje);
    if (!t(benForm.dni)) e.dni = "Requerido";
    if (!t(benForm.n1)) e.n1 = "Requerido";
    if (!t(benForm.a1)) e.a1 = "Requerido";
    if (!t(benForm.parentesco)) e.parentesco = "Requerido";
    if (!pctRaw) e.porcentaje = "Requerido";
    if (!e.porcentaje && !/^\d+$/.test(pctRaw))
      e.porcentaje = "Solo números enteros";
    const pct = Number(pctRaw);
    const totalOther = beneficiarios.reduce(
      (acc, b) => (b.id === benForm.id ? acc : acc + n(b.porcentaje, 0)),
      0,
    );
    if (
      !e.porcentaje &&
      (!Number.isFinite(pct) || !Number.isInteger(pct) || pct <= 0 || pct > 100)
    )
      e.porcentaje = "Debe ser un entero entre 1 y 100";
    else if (totalOther + pct > 100) e.porcentaje = "Total supera 100";
    setBenErr(e);
    if (Object.keys(e).length) return;
    const row = {
      ...benForm,
      n1: normalizeNombre(benForm.n1),
      n2: normalizeNombre(benForm.n2),
      a1: normalizeNombre(benForm.a1),
      a2: normalizeNombre(benForm.a2),
      id: benForm.id || localId(),
      porcentaje: pct,
    };
    setBeneficiarios((prev) => {
      const idx = prev.findIndex((x) => x.id === row.id);
      if (idx < 0) return [...prev, row];
      const next = [...prev];
      next[idx] = row;
      return next;
    });
    setDirty(true);
    setOpenBen(false);
  };

  const removeLocalBen = (id) => {
    setBeneficiarios((prev) => prev.filter((x) => x.id !== id));
    setDirty(true);
  };

  const closePreviewAdjunto = () => {
    setPreviewAdjuntoOpen(false);
    setPreviewAdjuntoLoading(false);
    setPreviewAdjuntoError("");
    if (previewAdjuntoUrl) {
      URL.revokeObjectURL(previewAdjuntoUrl);
      setPreviewAdjuntoUrl("");
    }
    setPreviewAdjuntoMime("");
  };

  const openPreviewAdjunto = async () => {
    const adjuntoActual = successData?.adjunto || null;
    const url = t(adjuntoActual?.downloadUrl);
    if (!url) {
      setError("No se encontro una ruta valida para el adjunto.");
      return;
    }
    if (previewAdjuntoUrl) {
      URL.revokeObjectURL(previewAdjuntoUrl);
      setPreviewAdjuntoUrl("");
    }
    setPreviewAdjuntoOpen(true);
    setPreviewAdjuntoLoading(true);
    setPreviewAdjuntoError("");
    setPreviewAdjuntoMime("");
    try {
      const token = getAccessToken(authToken);
      const headers = token ? { Authorization: token } : {};
      const response = await fetch(url, { method: "GET", headers });
      if (!response.ok)
        throw new Error(
          `No se pudo abrir el adjunto (HTTP ${response.status}).`,
        );
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewAdjuntoUrl(blobUrl);
      setPreviewAdjuntoMime(blob?.type || "application/octet-stream");
    } catch (e) {
      setPreviewAdjuntoError(e?.message || "No se pudo abrir el adjunto.");
      setPreviewAdjuntoOpen(false);
    } finally {
      setPreviewAdjuntoLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessOpen(false);
    setSuccessData(null);
    closePreviewAdjunto();
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.2 },
        background:
          "linear-gradient(180deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.08) 100%)",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      <Paper
        sx={{ p: 2, mb: 1.3, border: "1px solid #dbe6f6", borderRadius: 3 }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1 }}>{titulo}</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto auto" },
            gap: 1,
            alignItems: "end",
          }}
        >
          <TextField
            size="small"
            label="DNI"
            value={formatIdentificacionVisual(dni)}
            onChange={(e) => setDni(stripIdentificacionVisual(e.target.value))}
            inputProps={{ maxLength: 15 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={buscar}
            disabled={loading}
            sx={{ bgcolor: UI_PRIMARY, color: "#fff" }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setDni("");
              setResult(null);
              setTokenInfo(null);
              setTokenSec(0);
              setAutoRefreshToken(false);
              setAdjunto(null);
              setAdjuntos([]);
              setSuccessOpen(false);
              setSuccessData(null);
              closePreviewAdjunto();
              setError("");
              setOk("");
            }}
            sx={{ borderColor: UI_PRIMARY, color: UI_PRIMARY }}
          >
            Limpiar
          </Button>
        </Box>
        {error && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {ok}
          </Alert>
        )}
      </Paper>

      {result && (
        <>
          <Paper
            sx={{
              p: 1.4,
              mb: 1.3,
              border: "1px solid #dbe6f6",
              borderRadius: 3,
            }}
          >
            <Stepper activeStep={step} alternativeLabel>
              {STEPS.map((s, i) => (
                <Step key={s} completed={i < step}>
                  <StepLabel>{s}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Paper
            sx={{ p: 2, mb: 1.3, border: "1px solid #dbe6f6", borderRadius: 3 }}
          >
            <Typography sx={{ fontWeight: 700, color: UI_PRIMARY }}>
              {t(
                [
                  result.primerNombre,
                  result.segundoNombre,
                  result.primerApellido,
                  result.segundoApellido,
                ]
                  .join(" ")
                  .replace(/\s+/g, " "),
              )}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#555" }}>
              DNI: {formatIdentificacionVisual(result.dni)}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: "#555",
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              Estado actual:{" "}
              <Chip
                size="small"
                color={stateColor(desSel?.estado)}
                label={desSel?.estado || "SIN ESTADO"}
              />
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#555" }}>
              Designacion activa N°:{" "}
              <strong>{t(desActiva?.numero ?? desActiva?.Numero) || "-"}</strong>
            </Typography>
            {!modoConsulta && (
              <>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    onClick={createProceso}
                    disabled={nuevaDesignacionDeshabilitada}
                    sx={{ bgcolor: UI_PRIMARY, color: "#fff" }}
                  >
                    {newLoading
                      ? "Creando..."
                      : tokenLoading
                        ? "Validando..."
                        : "Nueva Designacion"}
                  </Button>
                  <FormControlLabel
                    sx={{ ml: 0.5 }}
                    control={
                      <Switch
                        size="small"
                        checked={autoRefreshToken}
                        onChange={(e) => setAutoRefreshToken(e.target.checked)}
                      />
                    }
                    label="Actualizar token automatico"
                  />
                </Box>
                {autoRefreshToken && (
                  <Typography sx={{ mt: 0.3, fontSize: 12.5, color: "#5f5f5f" }}>
                    Consulta automatica de token activa (cada 5 segundos).
                  </Typography>
                )}
                <Alert severity={tokenVigente ? "success" : "info"} sx={{ mt: 1 }}>
                  {tokenVigente
                    ? `Token vigente. Expira en ${tokenSec}s.`
                    : "No hay token vigente. Verifique huella."}
                </Alert>
              </>
            )}
          </Paper>

          {!ocultarDesignacionPorConfidencialidad && (
            <Paper
              sx={{ p: 2, mb: 1.3, border: "1px solid #dbe6f6", borderRadius: 3 }}
            >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: "1px solid #e5e5e5", mb: 1 }}
            >
              <Tab label="Designacion Actual" />
              <Tab label="Historico" />
            </Tabs>

            {tab === 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    Designacion N° {t(desSel?.numero ?? desSel?.Numero) || "-"}{" "}
                    ({desSel?.estado || "SIN ESTADO"})
                  </Typography>
                  {mostrarAccionesEdicion && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddCircleIcon />}
                        onClick={openAdd}
                        disabled={
                          desSel?.estado !== "EN PROCESO" || totalPct >= 100
                        }
                        sx={{ borderColor: UI_PRIMARY, color: UI_PRIMARY }}
                      >
                        Agregar beneficiario
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveBeneficiarios}
                        disabled={
                          saveLoading || desSel?.estado !== "EN PROCESO"
                        }
                        sx={{ bgcolor: UI_PRIMARY, color: "#fff" }}
                      >
                        {saveLoading ? "Guardando..." : "Guardar beneficiarios"}
                      </Button>
                    </Box>
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>
                        No. Identificacion
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Primer Nombre
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Segundo Nombre
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Primer Apellido
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Segundo Apellido
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Parentesco</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Porcentaje</TableCell>
                      {mostrarAccionesEdicion && (
                        <TableCell sx={{ fontWeight: 700 }}>Detalle</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!beneficiarios.length ? (
                      <TableRow>
                        <TableCell colSpan={mostrarAccionesEdicion ? 8 : 7}>
                          Sin beneficiarios.
                        </TableCell>
                      </TableRow>
                    ) : (
                      beneficiarios.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell>
                            {formatIdentificacionVisual(b.dni)}
                          </TableCell>
                          <TableCell>{b.n1}</TableCell>
                          <TableCell>{b.n2}</TableCell>
                          <TableCell>{b.a1}</TableCell>
                          <TableCell>{b.a2}</TableCell>
                          <TableCell>
                            {getParentescoLabel(b.parentesco)}
                          </TableCell>
                          <TableCell>{n(b.porcentaje)}%</TableCell>
                          {mostrarAccionesEdicion && (
                            <TableCell>
                              <Tooltip title="Editar">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openEdit(b)}
                                    disabled={
                                      desSel?.estado !== "EN PROCESO"
                                    }
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeLocalBen(b.id)}
                                    disabled={
                                      desSel?.estado !== "EN PROCESO"
                                    }
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                <Box
                  sx={{
                    mt: 1.2,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      Total porcentaje legal: {totalPct}%
                    </Typography>
                    {mostrarAccionesEdicion && (
                      <Typography sx={{ fontSize: 12.5, color: "#5f5f5f" }}>
                        Imprimir y adjuntar se habilitan al guardar beneficiarios
                        con 100%.
                      </Typography>
                    )}
                    {mostrarAccionesEdicion && dirty && (
                      <Typography sx={{ fontSize: 12.5, color: "#bf6500" }}>
                        Tiene cambios sin guardar.
                      </Typography>
                    )}
                  </Box>
                  {mostrarAccionesEdicion && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={printNow}
                        disabled={
                          printLoading ||
                          step !== 3 ||
                          !accionesFinalesHabilitadas
                        }
                        sx={{ borderColor: UI_PRIMARY, color: UI_PRIMARY }}
                      >
                        {printLoading ? "Abriendo..." : "Visualizar / Imprimir"}
                      </Button>
                      <input
                        ref={fileRef}
                        type="file"
                        hidden
                        onChange={handleAdjuntoSeleccionado}
                      />
                      <Button
                        variant="outlined"
                        onClick={abrirSelectorAdjunto}
                        disabled={
                          uploadLoading ||
                          step !== 3 ||
                          !accionesFinalesHabilitadas
                        }
                        sx={{ borderColor: UI_PRIMARY, color: UI_PRIMARY }}
                      >
                        {adjunto ? "Cambiar adjunto" : "Seleccionar adjunto"}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={adjuntarFinal}
                        disabled={
                          uploadLoading || !accionesFinalesHabilitadas || !adjunto
                        }
                        sx={{ bgcolor: UI_PRIMARY, color: "#fff" }}
                      >
                        {uploadLoading ? "Adjuntando..." : "Adjuntar y finalizar"}
                      </Button>
                    </Box>
                  )}
                </Box>
                {mostrarAccionesEdicion && (
                  <Typography sx={{ mt: 0.8, fontSize: 12.5, color: "#5f5f5f" }}>
                    Archivo seleccionado: {adjunto?.name || "Ninguno"}
                  </Typography>
                )}
              </>
            )}

            {tab === 1 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>N°</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fecha Inicio</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fecha Fin</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!designaciones.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>Sin historico.</TableCell>
                    </TableRow>
                  ) : (
                    designaciones.map((d) => (
                      <TableRow
                        key={d.idDesignacion}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => setDesignacionId(d.idDesignacion)}
                      >
                        <TableCell>
                          {t(d?.numero ?? d?.Numero) || "-"}
                        </TableCell>
                        <TableCell>
                          {formatFechaHistorico(d.fechaInicio)}
                        </TableCell>
                        <TableCell>
                          {formatFechaHistorico(d.fechaFinalizo)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={stateColor(d.estado)}
                            label={d.estado || "SIN ESTADO"}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
            </Paper>
          )}
        </>
      )}

      {!modoConsulta && (
        <Dialog
          open={openBen}
          onClose={() => setOpenBen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {benForm.id
              ? "Editar beneficiario legal"
              : "Agregar beneficiario legal"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1,
                pt: 0.6,
              }}
            >
              <TextField
                size="small"
                required
                label="No. Identificacion"
                value={formatIdentificacionVisual(benForm.dni)}
                onChange={(e) =>
                  setBenForm((p) => ({
                    ...p,
                    dni: e.target.value.replace(/\D/g, ""),
                  }))
                }
                error={Boolean(benErr.dni)}
                helperText={benErr.dni || ""}
                inputProps={{ inputMode: "numeric" }}
              />
              <TextField
                size="small"
                select
                label="Parentesco"
                value={benForm.parentesco}
                onChange={(e) =>
                  setBenForm((p) => ({ ...p, parentesco: e.target.value }))
                }
                error={Boolean(benErr.parentesco)}
                helperText={benErr.parentesco || ""}
                disabled={loadingParentescos}
              >
                <MenuItem value="">
                  <em>
                    {loadingParentescos
                      ? "Cargando parentescos..."
                      : "Seleccione parentesco"}
                  </em>
                </MenuItem>
                {benForm.parentesco &&
                  !parentescoOptions.some(
                    (option) =>
                      String(option.value) === String(benForm.parentesco),
                  ) && (
                    <MenuItem value={benForm.parentesco}>
                      {benForm.parentesco}
                    </MenuItem>
                  )}
                {parentescoOptions.map((option) => (
                  <MenuItem
                    key={`parentesco-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                required
                label="Primer nombre"
                value={benForm.n1}
                onChange={(e) =>
                  setBenForm((p) => ({
                    ...p,
                    n1: normalizeNombreInput(e.target.value),
                  }))
                }
                onBlur={handleBenNombreBlur("n1")}
                error={Boolean(benErr.n1)}
                helperText={benErr.n1 || ""}
              />
              <TextField
                size="small"
                label="Segundo nombre"
                value={benForm.n2}
                onChange={(e) =>
                  setBenForm((p) => ({
                    ...p,
                    n2: normalizeNombreInput(e.target.value),
                  }))
                }
                onBlur={handleBenNombreBlur("n2")}
              />
              <TextField
                size="small"
                required
                label="Primer apellido"
                value={benForm.a1}
                onChange={(e) =>
                  setBenForm((p) => ({
                    ...p,
                    a1: normalizeNombreInput(e.target.value),
                  }))
                }
                onBlur={handleBenNombreBlur("a1")}
                error={Boolean(benErr.a1)}
                helperText={benErr.a1 || ""}
              />
              <TextField
                size="small"
                label="Segundo apellido"
                value={benForm.a2}
                onChange={(e) =>
                  setBenForm((p) => ({
                    ...p,
                    a2: normalizeNombreInput(e.target.value),
                  }))
                }
                onBlur={handleBenNombreBlur("a2")}
              />
              <TextField
                size="small"
                required
                type="text"
                label="Porcentaje"
                value={benForm.porcentaje}
                onChange={(e) =>
                  setBenForm((p) => ({
                    ...p,
                    porcentaje: e.target.value.replace(/\D/g, ""),
                  }))
                }
                error={Boolean(benErr.porcentaje)}
                helperText={benErr.porcentaje || ""}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  min: 1,
                  max: 100,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.5 }}>
            <Button onClick={() => setOpenBen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={saveLocalBen}
              sx={{ bgcolor: UI_PRIMARY, color: "#fff" }}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={successOpen} onClose={handleCloseSuccess} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pt: 3, pb: 1 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <Typography sx={{ color: "#fff", fontSize: 36, lineHeight: 1 }}>
              ✓
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Designacion finalizada
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ pb: 1.5 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.3 }}>
            La designacion de beneficiarios legales de{" "}
            <strong>{successData?.personaName || "la persona"}</strong> se
            finalizo correctamente.
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "#4f4f4f", mb: 1.2 }}>
            Designacion N°: <strong>{successData?.designacionNumero || "-"}</strong>
          </Typography>

          <Typography sx={{ fontWeight: 700, mb: 0.7 }}>
            Resumen de beneficiarios
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>No. Identificacion</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nombre completo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Parentesco</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Porcentaje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!successData?.beneficiarios?.length ? (
                <TableRow>
                  <TableCell colSpan={4}>Sin beneficiarios.</TableCell>
                </TableRow>
              ) : (
                successData.beneficiarios.map((item) => (
                  <TableRow key={`success-ben-${item.id}`}>
                    <TableCell>
                      {formatIdentificacionVisual(t(item?.dni))}
                    </TableCell>
                    <TableCell>
                      {`${t(item?.n1)} ${t(item?.n2)} ${t(item?.a1)} ${t(item?.a2)}`
                        .replace(/\s+/g, " ")
                        .trim() || "-"}
                    </TableCell>
                    <TableCell>{getParentescoLabel(item?.parentesco)}</TableCell>
                    <TableCell>{n(item?.porcentaje, 0)}%</TableCell>
                  </TableRow>
                ))
              )}
              {Boolean(successData?.beneficiarios?.length) && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "right", fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {n(successData?.totalPorcentaje, 0)}%
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box sx={{ mt: 1.3 }}>
            <Typography sx={{ fontWeight: 700 }}>Adjunto cargado</Typography>
            <Typography sx={{ fontSize: 13.5, color: "#4f4f4f" }}>
              {t(successData?.adjunto?.nombre) || "No se pudo identificar el archivo."}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={openPreviewAdjunto}
              disabled={!t(successData?.adjunto?.downloadUrl)}
              sx={{ mt: 0.7, borderColor: UI_PRIMARY, color: UI_PRIMARY }}
            >
              Visualizar adjunto
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button variant="contained" onClick={handleCloseSuccess} sx={{ px: 5, bgcolor: UI_PRIMARY }}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewAdjuntoOpen}
        onClose={closePreviewAdjunto}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle sx={{ pb: 1 }}>Vista previa de adjunto</DialogTitle>
        <DialogContent dividers>
          {previewAdjuntoLoading ? (
            <Typography sx={{ color: "#5f5f5f" }}>Cargando adjunto...</Typography>
          ) : previewAdjuntoError ? (
            <Alert severity="error">{previewAdjuntoError}</Alert>
          ) : previewAdjuntoUrl ? (
            <>
              <iframe
                title="adjunto-preview"
                src={
                  previewAdjuntoEsPdf
                    ? `${previewAdjuntoUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`
                    : previewAdjuntoUrl
                }
                style={{ width: "100%", height: "70vh", border: "none" }}
              />
              <Typography sx={{ mt: 1, fontSize: 12.5, color: "#5f5f5f" }}>
                Tipo detectado: {previewAdjuntoMime || "no identificado"}
              </Typography>
            </>
          ) : (
            <Alert severity="info">No hay archivo para mostrar.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={closePreviewAdjunto}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
